// Изменения «Запуска» в пульте (задача D15; сервер и правила — D14, approved-plan «Опасные действия»).
//
// - Снижение (доля меньше, канал выключен, ступень ниже, остановка, лимиты, дежурные) уходит сразу: `POST
//   /api/v1/launch/changes` без `confirmed`, потом тост «Отменить» на 10 с. Сервер держит отмену 15 с, но откатывает
//   только последнее изменение (иначе `undo_conflict`), поэтому «Отменить» на экране одно: новый тост заменяет прежний,
//   и тост закрывается не позже, чем за секунду до конца срока, даже если таймер Sonner стоял на паузе (наведение,
//   скрытая вкладка). «Отменить» — `POST /api/v1/undo { undoId }`, тост «Отменено» и перечитать `/launch`; опоздали —
//   сервер отвечает 410, его текст «Отменить уже нельзя: прошло больше 15 с» — в тосте.
// - Повышение (доля больше, канал включён, ступень выше, «Включить снова») экран сначала показывает окном (`riskOfChange`
//   — копия правил сервера `riskOf`), а после «Поднять» / «Включить» / «Перейти» шлёт с `confirmed: true`; тост «Решение
//   записано в журнал». Ошибётся пульт в оценке — сервер ответит 409 `confirmation_required`, и экран откроет то же окно
//   (`'needs_confirmation'`).
// - Только правда сервера: контрол меняется, когда пришёл ответ, — новое состояние из ответа сразу в кэше; статус Анны
//   (пилюля) и «Готовность» перечитываются.
// - Версия: `expectedVersion` — `version` того состояния, которое видел человек. Изменения идут по одному: следующее ждёт
//   ответа на предыдущее и берёт уже новую версию из кэша — три быстрых выключения каналов не спорят друг с другом.
//   Изменили в другом окне — 409 `version_conflict`: тост «Настройки уже изменились в другом окне — обновлено» и
//   перезапрос. Выключение канала не ждёт человека: перечитывает состояние и повторяет один раз само (`retryOnConflict`);
//   повышение не повторяется никогда. Остановку сервер принимает без сверки версии (Ruling 130): «Остановить» и
//   «Остановить везде» шлют версию из кэша, а если экран «Запуск» ещё не читали — 0, не дожидаясь `GET /launch` (он ждёт
//   имена дежурных из Омнидеска до 8 с).
// - Остановку объявляет чтению с экрана пилюля статуса (`assertive`, `noteStoppedHere`): у Sonner одна область тостов,
//   и она `polite`.
// - Запросы идут как мутации TanStack (`MutationObserver`): кэш знает, что изменение в пути (`useIsMutating`), а тесты
//   дожидаются его до уборки.
import {
  CHANNEL_MODE_RU,
  CHANNEL_MODES,
  LaunchChangeResponseSchema,
  STAGE_ORDER,
  STAGE_RU,
  UndoResponseSchema,
  type LaunchChange,
  type LaunchChangeBody,
  type LaunchChangeResponse,
  type LaunchRisk,
  type LaunchState,
} from '@contract/index.js';
import { MutationObserver, useIsMutating, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { apiSend } from '@/api/client';
import { ApiError } from '@/api/errors';
import { qk } from '@/api/keys';
import { formatInt } from '@/lib/format';
import { noteStoppedHere } from '@/shell/stop-announcement';
import { toastActionError, toastError, toastUndo } from '@/shell/toaster';
import { LAUNCH_CHANGES_PATH, launchQuery, UNDO_PATH } from './queries';

/** Итог изменения: применено; нужно окно подтверждения; версия устарела; отказ (о нём уже сказано). */
export type LaunchChangeOutcome = 'done' | 'needs_confirmation' | 'conflict' | 'refused';

/** Сколько тост держит «Отменить» в «Запуске», мс (сервер — 15 с). */
export const LAUNCH_UNDO_TOAST_MS = 10_000;
/** Запас до конца срока отмены, когда тост закрывается сам, даже на паузе, мс. */
const UNDO_DEADLINE_MARGIN_MS = 1000;

export const VERSION_CONFLICT_TOAST_RU = 'Настройки уже изменились в другом окне — обновлено';
/** Тост о конфликте версии — чуть дольше успеха: изменение не применено, это надо успеть прочитать. */
const VERSION_CONFLICT_TOAST_MS = 6000;
export const RAISE_DONE_RU = 'Решение записано в журнал';
export const RAISE_BODY_RU = 'Решение запишется в журнал.';
export const UNDONE_RU = 'Отменено';

/** Ключи мутаций: изменение и отмена. */
export const LAUNCH_CHANGE_KEY = ['launch', 'change'] as const;
const LAUNCH_UNDO_KEY = ['launch', 'undo'] as const;

const NBSP = ' ';

/** В кавычках «ёлочках»; кавычки внутри — «лапки». */
export function quoteRu(text: string): string {
  return `«${text.replaceAll('«', '„').replaceAll('»', '“')}»`;
}

/** «10 %» — с неразрывным пробелом. */
export function percentRu(n: number): string {
  return `${formatInt(n)}${NBSP}%`;
}

function channelLabelOf(state: Pick<LaunchState, 'channels'> | undefined, key: string): string {
  return state?.channels.find((channel) => channel.key === key)?.labelRu ?? key;
}

function isHigher<T>(order: readonly T[], to: T, from: T): boolean {
  return order.indexOf(to) > order.indexOf(from);
}

/** Сюда не попасть: все виды изменений разобраны выше. Новый вид — ошибка типов здесь, а не тихий пропуск. */
function unknownKind(change: never): never {
  throw new Error(`Неизвестный вид изменения «Запуска»: ${JSON.stringify(change)}`);
}

/**
 * Риск изменения — те же правила, что у сервера (`riskOf`, D14): выше по ступени, доле или режиму канала — повышение;
 * «Включить снова» — повышение; остановка, лимиты и дежурные — снижение. Канала нет в состоянии — считаем его в тени
 * (умолчание `channelDefault` A9); ошибётся пульт — сервер ответит `confirmation_required`.
 */
export function riskOfChange(change: LaunchChange, state: LaunchState): LaunchRisk {
  switch (change.kind) {
    case 'stage':
      return isHigher(STAGE_ORDER, change.to, state.stage) ? 'raise' : 'safe';
    case 'share':
      return change.to > state.sharePercent ? 'raise' : 'safe';
    case 'channel': {
      const from = state.channels.find((channel) => channel.key === change.key)?.mode ?? 'shadow';
      return isHigher(CHANNEL_MODES, change.to, from) ? 'raise' : 'safe';
    }
    case 'resume':
      return 'raise';
    case 'stop_all':
    case 'duty':
    case 'budget':
      return 'safe';
    default:
      return unknownKind(change);
  }
}

/** Тост снижения (с «Отменить»). Состояние — только ради подписи канала. */
export function safeToastRu(change: LaunchChange, state?: Pick<LaunchState, 'channels'>): string {
  switch (change.kind) {
    case 'share':
      return `Доля снижена до ${percentRu(change.to)}`;
    case 'channel': {
      const label = quoteRu(channelLabelOf(state, change.key));
      return change.to === 'off' ? `Канал ${label} выключен` : `Канал ${label}: ${CHANNEL_MODE_RU[change.to]}`;
    }
    case 'stage':
      return `Ступень: ${STAGE_RU[change.to]}`;
    case 'stop_all':
      return 'Анна остановлена';
    case 'budget':
      return 'Лимиты сохранены';
    case 'duty':
      return 'Дежурные сохранены';
    case 'resume':
      return RAISE_DONE_RU;
    default:
      return unknownKind(change);
  }
}

/** Начало текста ошибки: «Не удалось изменить долю: {текст сервера}». Состояние — только ради подписи канала. */
export function failLeadRu(change: LaunchChange, state?: Pick<LaunchState, 'channels'>): string {
  switch (change.kind) {
    case 'share':
      return 'Не удалось изменить долю';
    case 'channel':
      return `Не удалось изменить канал ${quoteRu(channelLabelOf(state, change.key))}`;
    case 'stage':
      return 'Не удалось сменить ступень';
    case 'stop_all':
      // Не «не удалось остановить»: после остановки не должно остаться сомнений, стоит Анна или нет.
      return 'Анна не остановлена';
    case 'resume':
      return 'Не удалось включить Анну';
    case 'budget':
      return 'Не удалось сохранить лимиты';
    case 'duty':
      return 'Не удалось сохранить дежурных';
    default:
      return unknownKind(change);
  }
}

/** Окно повышения: заголовок, абзацы и подпись кнопки (сводку ворот добавит D16). */
export interface RaiseCopy {
  readonly title: string;
  readonly body: readonly string[];
  readonly confirmRu: string;
}

export function raiseCopyOf(change: LaunchChange, state: LaunchState): RaiseCopy {
  switch (change.kind) {
    case 'stage':
      return { title: `Перейти на ${quoteRu(STAGE_RU[change.to])}?`, body: [RAISE_BODY_RU], confirmRu: 'Перейти' };
    case 'share':
      return { title: `Поднять долю до ${percentRu(change.to)}?`, body: [RAISE_BODY_RU], confirmRu: 'Поднять' };
    case 'channel': {
      const label = quoteRu(channelLabelOf(state, change.key));
      const from = state.channels.find((channel) => channel.key === change.key)?.mode ?? 'off';
      const title =
        from === 'off' ? `Включить канал ${label}?` : `Включить ${quoteRu(CHANNEL_MODE_RU[change.to])} в канале ${label}?`;
      return { title, body: [RAISE_BODY_RU], confirmRu: 'Включить' };
    }
    case 'resume': {
      const stage = state.stopped?.resumeStage ?? state.stage;
      return {
        title: 'Включить Анну снова?',
        body: [`Вернётся режим ${quoteRu(STAGE_RU[stage])} на всех каналах, как до остановки.`, RAISE_BODY_RU],
        confirmRu: 'Включить',
      };
    }
    case 'stop_all':
    case 'duty':
    case 'budget':
      return { title: 'Подтвердить изменение?', body: [RAISE_BODY_RU], confirmRu: 'Подтвердить' };
    default:
      return unknownKind(change);
  }
}

/** Изменение уже не нужно: канал, который выключают, уже выключен (стало ясно после перечитывания). */
function alreadyDone(change: LaunchChange, state: LaunchState): boolean {
  if (change.kind === 'channel') return state.channels.find((channel) => channel.key === change.key)?.mode === change.to;
  return false;
}

/** Изменение, которое ничего не поменяло (сервер не записал строку журнала). */
function unchangedRu(change: LaunchChange, state: LaunchState): string {
  if (change.kind === 'stop_all') return 'Анна уже остановлена';
  if (change.kind === 'channel' && change.to === 'off') return `Канал ${quoteRu(channelLabelOf(state, change.key))} уже выключен`;
  return 'Без изменений: так уже настроено';
}

/** Запрос как мутация TanStack: его видят `useIsMutating` и `isMutating()` кэша. */
function runMutation<T>(queryClient: QueryClient, key: readonly string[], fn: () => Promise<T>): Promise<T> {
  return new MutationObserver<T, unknown>(queryClient, { mutationKey: key, mutationFn: fn }).mutate();
}

export interface ApplyLaunchChangeOptions {
  /**
   * Состояние, которое видел человек: от него версия, подписи и риск. Нет его только у остановки, пока экран «Запуск» не
   * читали (`stopEverywhere`): версия тогда 0 — у остановки сервер её не сверяет (Ruling 130).
   */
  readonly state: LaunchState | undefined;
  /** Повышение подтверждено в окне. */
  readonly confirmed?: boolean;
  /**
   * Сбой, о котором вызывающий скажет сам (форма лимитов — под полем, окно повышения — строкой в окне): `true` — сказал,
   * тоста не нужно. `leadRu` — начало текста («Не удалось изменить долю»).
   */
  readonly onFailure?: (error: unknown, leadRu: string) => boolean;
  /** Версия устарела — перечитать состояние и повторить один раз, без тоста о конфликте (только снижения-выключения). */
  readonly retryOnConflict?: boolean;
}

/** Изменения по одному на кэш: следующее ждёт ответа на предыдущее. */
const queues = new WeakMap<QueryClient, Promise<unknown>>();

/** Тост с «Отменить» на экране один: новое снижение закрывает прежний (сервер откатит только последнее). */
let undoToast: string | number | null = null;

function showUndo(queryClient: QueryClient, change: LaunchChange, o: ApplyLaunchChangeOptions, response: LaunchChangeResponse, undoId: string): void {
  if (undoToast !== null) toast.dismiss(undoToast);
  const id = toastUndo({
    message: safeToastRu(change, o.state),
    durationMs: LAUNCH_UNDO_TOAST_MS,
    onUndo: () => {
      void undoLaunchChange(queryClient, undoId);
    },
  });
  undoToast = id;
  // Срок сервера считаем по его же часам: `undoUntil` против момента ответа, а не против часов браузера.
  if (response.undoUntil !== null) {
    const left = Date.parse(response.undoUntil) - Date.parse(response.state.at) - UNDO_DEADLINE_MARGIN_MS;
    setTimeout(
      () => {
        toast.dismiss(id);
        if (undoToast === id) undoToast = null;
      },
      Math.max(left, 0),
    );
  }
}

/** Отменяет снижение по `undoId` (см. шапку файла). */
export async function undoLaunchChange(queryClient: QueryClient, undoId: string): Promise<void> {
  try {
    await runMutation(queryClient, LAUNCH_UNDO_KEY, () => apiSend('POST', UNDO_PATH, { undoId }, UndoResponseSchema));
  } catch (error) {
    if (error instanceof ApiError && error.status < 500) {
      // 410 «Отменить уже нельзя: прошло больше 15 с», 409 «Запись уже изменилась — отмена не применена»: повторять нечего.
      toastError(error.messageRu);
      void queryClient.invalidateQueries({ queryKey: qk.launch });
      return;
    }
    toastActionError('Не удалось отменить', error, () => {
      void undoLaunchChange(queryClient, undoId);
    });
    return;
  }
  toast.success(UNDONE_RU);
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: qk.launch }),
    queryClient.invalidateQueries({ queryKey: qk.status }),
    queryClient.invalidateQueries({ queryKey: qk.readiness }),
  ]);
}

async function failed(
  queryClient: QueryClient,
  change: LaunchChange,
  o: ApplyLaunchChangeOptions,
  error: unknown,
): Promise<LaunchChangeOutcome> {
  if (error instanceof ApiError) {
    if (error.code === 'confirmation_required') return 'needs_confirmation';
    if (error.code === 'version_conflict') {
      if (o.retryOnConflict === true) {
        const fresh = await queryClient.fetchQuery({ ...launchQuery, staleTime: 0 }).catch(() => null);
        if (fresh !== null) {
          if (alreadyDone(change, fresh)) {
            toast(unchangedRu(change, fresh));
            return 'done';
          }
          return send(queryClient, change, { ...o, state: fresh, retryOnConflict: false }, fresh.version);
        }
      }
      toast.warning(VERSION_CONFLICT_TOAST_RU, { duration: VERSION_CONFLICT_TOAST_MS });
      void queryClient.invalidateQueries({ queryKey: qk.launch });
      return 'conflict';
    }
    // Права сменились, пока экран открыт: перечитать, кто вошёл, — экран перерисуется по новой роли.
    if (error.status === 403) void queryClient.invalidateQueries({ queryKey: qk.me });
  }
  const leadRu = failLeadRu(change, o.state);
  if (o.onFailure?.(error, leadRu) === true) return 'refused';
  // Ответ сервиса 4xx — правило или значение: повтор ничего не изменит. 5xx (и 502 Омнидеска у дежурных) и обрыв — с
  // «Повторить» (при обрыве он один — в баннере «Нет связи»).
  if (error instanceof ApiError && error.status < 500) {
    toastError(`${leadRu}: ${error.messageRu}`);
    return 'refused';
  }
  toastActionError(leadRu, error, () => {
    void applyLaunchChange(queryClient, change, { ...o, state: queryClient.getQueryData(launchQuery.queryKey) ?? o.state });
  });
  return 'refused';
}

async function send(
  queryClient: QueryClient,
  change: LaunchChange,
  o: ApplyLaunchChangeOptions,
  expectedVersion: number,
): Promise<LaunchChangeOutcome> {
  const body: LaunchChangeBody = { change, expectedVersion, ...(o.confirmed === true ? { confirmed: true } : {}) };
  // Перечитывание, начатое до изменения (опрос, поток), не должно лечь поверх ответа старыми данными.
  await queryClient.cancelQueries({ queryKey: qk.launch });
  let response: LaunchChangeResponse;
  try {
    response = await runMutation(queryClient, LAUNCH_CHANGE_KEY, () =>
      apiSend('POST', LAUNCH_CHANGES_PATH, body, LaunchChangeResponseSchema),
    );
  } catch (error) {
    return failed(queryClient, change, o, error);
  }
  queryClient.setQueryData(launchQuery.queryKey, response.state);
  void queryClient.invalidateQueries({ queryKey: qk.status });
  void queryClient.invalidateQueries({ queryKey: qk.readiness });
  if (response.risk === 'raise') {
    toast.success(RAISE_DONE_RU);
    return 'done';
  }
  const { undoId } = response;
  if (undoId === null) {
    toast(unchangedRu(change, response.state));
    return 'done';
  }
  if (change.kind === 'stop_all') noteStoppedHere();
  showUndo(queryClient, change, o, response, undoId);
  return 'done';
}

/**
 * Изменение «Запуска» (правила — в шапке файла). Повышение без `confirmed` пульт сам не шлёт — экран сначала открывает окно;
 * если сервер всё же попросил подтверждения — `'needs_confirmation'`.
 */
export async function applyLaunchChange(
  queryClient: QueryClient,
  change: LaunchChange,
  o: ApplyLaunchChangeOptions,
): Promise<LaunchChangeOutcome> {
  const previous = queues.get(queryClient);
  const run = (async () => {
    if (previous !== undefined) await previous.catch(() => undefined);
    // Ждали своё же изменение — версия уже новая, она в кэше.
    const seen = o.state?.version ?? 0;
    const version = previous === undefined ? seen : (queryClient.getQueryData(launchQuery.queryKey)?.version ?? seen);
    return send(queryClient, change, o, version);
  })();
  queues.set(queryClient, run);
  try {
    return await run;
  } finally {
    if (queues.get(queryClient) === run) queues.delete(queryClient);
  }
}

/**
 * «Остановить» («Запуск») и «Остановить везде» (пилюля): сразу, без чтения «Запуска» — версию остановки сервер не сверяет
 * (Ruling 130), а состояние «Запуска» ждёт имена дежурных из Омнидеска до 8 с. Версия — из кэша, а если экран её ещё не
 * читал — 0.
 */
export async function stopEverywhere(queryClient: QueryClient): Promise<LaunchChangeOutcome> {
  return applyLaunchChange(queryClient, { kind: 'stop_all' }, { state: queryClient.getQueryData(launchQuery.queryKey) });
}

/** Изменения «Запуска» в компоненте: `apply` с подписью брифа D15 и `pending`, пока изменение в пути. */
export function useLaunchChange(): {
  readonly apply: (change: LaunchChange, o: ApplyLaunchChangeOptions) => Promise<LaunchChangeOutcome>;
  readonly pending: boolean;
} {
  const queryClient = useQueryClient();
  const pending = useIsMutating({ mutationKey: LAUNCH_CHANGE_KEY }) > 0;
  const apply = useCallback(
    (change: LaunchChange, o: ApplyLaunchChangeOptions) => applyLaunchChange(queryClient, change, o),
    [queryClient],
  );
  return { apply, pending };
}
